import { nanoid } from 'nanoid';
import { useEnvPropsStore } from '@/stores/envProps';
import { useHead } from '#app';

const SESSION_STORAGE_KEY = 'beaconToken';

/**
 * Composable for managing a unique user session ID.
 * The ID is persisted in sessionStorage.
 */
export const useUserSessionId = () => {
  const isRiskAdviseEnabled = () => {
    const envPropsStore = useEnvPropsStore();
    return import.meta.client && envPropsStore.envProps?.killSwitches?.preTransactionRiskAdviseEnabled;
  };

  const postToRiskified = (sessionId) => {
    return new Promise((resolve, reject) => {
      const url = `https://beacon.riskified.com?shop=${window.location.host}&sid=${sessionId}`;

      useHead({
        script: [
          {
            src: url,
            async: true,
            onload: () => {
              resolve();
            },
            onerror: (error) => {
              reject(error);
            },
          },
        ],
      });
    });
  };

  /**
   * Retrieves the session ID from sessionStorage.
   * @returns {string | null} The session ID or null if not set.
   */
  const getId = () => sessionStorage.getItem(SESSION_STORAGE_KEY);

  /**
   * Generates and stores a new session ID.
   * @returns {string} The newly created session ID.
   */
  const setId = () => {
    const id = nanoid();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  };

  /**
   * Initializes the session ID if it doesn't exist.
   * @returns {string} The existing or newly created session ID.
   */
  const userSessionIdInit = () => {
    if (isRiskAdviseEnabled()) {
      const sessionId = getId() || setId();
      postToRiskified(sessionId);
    }
  };

  const enhanceRiskifiedPayload = async (payload) => {
    if (!isRiskAdviseEnabled()) return false;

    let sessionId = getId();
    if (!sessionId) {
      await userSessionIdInit();
      sessionId = getId();
    }

    if (!payload.tender) return false;

    payload.tender.cartToken = sessionId;
    return true;
  };

  return { getId, setId, userSessionIdInit, postToRiskified, enhanceRiskifiedPayload };
};

export default useUserSessionId;
